import { Autocomplete, Box, Paper, TextField, Typography } from '@mui/material'

import type { ArticleFormState } from '../types/article-form.types'
import type { User } from '../../auth/types/auth.types'

type WorkflowUser = Pick<User, 'id' | 'name' | 'email' | 'role'>

type ArticleWorkflowSectionProps = {
  currentUserRole?: User['role']
  form: ArticleFormState
  isEditing: boolean
  setField: <K extends keyof ArticleFormState>(field: K) => (value: ArticleFormState[K]) => void
  workflowUsers: WorkflowUser[]
}

export function ArticleWorkflowSection({
  currentUserRole,
  form,
  isEditing,
  setField,
  workflowUsers,
}: ArticleWorkflowSectionProps) {
  const canEditAuthor = currentUserRole === 'admin' || currentUserRole === 'publisher'
  const canEditReviewer = currentUserRole === 'admin'
  const reviewerOptions = workflowUsers
    .filter((option) => option.role === 'publisher' || option.role === 'admin')
    .sort((a, b) => {
      if (a.role === b.role) {
        return a.name.localeCompare(b.name)
      }
      return a.role === 'publisher' ? -1 : 1
    })
  const selectedAuthor = workflowUsers.find((option) => option.id === form.author_id) || null
  const selectedReviewer = reviewerOptions.find((option) => option.id === form.reviewer_id) || null

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" color="primary" mb={0.5}>
        5. Workflow
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Assignment and control fields for the review and approval process.
      </Typography>
      <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={2}>
        <TextField
          label="Date Created"
          fullWidth
          value={isEditing ? '' : 'Auto-filled on save'}
          InputProps={{ readOnly: true }}
          sx={{ '& input': { bgcolor: '#f8fafc', color: '#475467' } }}
        />
        <TextField
          label="Workflow Status"
          fullWidth
          value="Draft → Under Review → Approved → Published"
          InputProps={{ readOnly: true }}
          sx={{ '& input': { bgcolor: '#f8fafc', color: '#475467' } }}
        />
        <TextField
          label="Final Publication Target"
          fullWidth
          value="Wiki.js Knowledge Base"
          InputProps={{ readOnly: true }}
          sx={{ '& input': { bgcolor: '#f8fafc', color: '#475467' } }}
        />
        {isEditing && (
          <>
            <Autocomplete
              options={workflowUsers}
              value={selectedAuthor}
              onChange={(_event, option) => setField('author_id')(option?.id || null)}
              getOptionLabel={(option) => `${option.name} (${option.email})`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disabled={!canEditAuthor}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Author"
                  helperText={canEditAuthor ? 'Editable by admin and publisher.' : 'Only admin and publisher can change author.'}
                />
              )}
            />
            <Autocomplete
              options={reviewerOptions}
              value={selectedReviewer}
              onChange={(_event, option) => setField('reviewer_id')(option?.id || null)}
              getOptionLabel={(option) => `${option.name} (${option.email})`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disabled={!canEditReviewer}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Reviewer"
                  helperText={canEditReviewer ? 'Editable by admin only.' : 'Only admin can change reviewer.'}
                />
              )}
            />
          </>
        )}
      </Box>
      <Box mt={2} p={1.5} sx={{ bgcolor: '#f8fafc', borderLeft: '4px solid #1f6fb2', borderRadius: 1, color: '#6b7280', fontSize: '0.8125rem' }}>
        Save Draft stores the record in the database. Submit for Review triggers workflow notification. Approved Technotes are published to Wiki.js.
      </Box>
    </Paper>
  )
}
